import {
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
} from "@mui/icons-material";
import {
  alpha,
  Avatar,
  Box,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { SalespersonActivityStats } from "../../../store/services/salesManagerService";
import {
  BACKLOG_TOOLTIP,
  CATEGORY_LABELS,
  formatPercent,
  getCompletionRateColor,
  getContactAttemptCount,
  getGrandTotal,
  getMotesbokningTotal,
  getPlaneringTotal,
  getSegmentTotal,
  OVERDUE_TOOLTIP,
  sumActivityCounts,
} from "../shared/breakdown";

const CONTACT_ATTEMPT_COLOR = "#9B6B00";

interface SalespersonTableProps {
  data: SalespersonActivityStats[];
  onRowClick: (stats: SalespersonActivityStats) => void;
}

type SortKey =
  | "name"
  | "existing"
  | "prospects"
  | "planering"
  | "motesbokning"
  | "contactAttempts"
  | "overdue"
  | "backlog";

type SortDir = "asc" | "desc";

interface Row {
  stats: SalespersonActivityStats;
  name: string;
  existing: number;
  prospects: number; // SuspectProspect + Pipeline summerat
  planering: number;
  planeringCompleted: number;
  planeringRate: number;
  motesbokning: number;
  motesbokningCompleted: number;
  motesbokningRate: number;
  contactAttempts: number; // effort-metrik, ej del av total
  total: number; // bara för procent-captions i segmentkolumnerna
  overdue: number;
  backlog: number;
}

const headCells: {
  id: SortKey;
  label: string;
  numeric: boolean;
  tooltip?: string;
}[] = [
  { id: "name", label: "Säljare", numeric: false },
  { id: "existing", label: "Befintliga", numeric: true },
  {
    id: "prospects",
    label: "Prospekt",
    numeric: true,
    tooltip:
      "Summan av Suspekt / Prospekt (11-24) och Pipeline (31, 41). Drilldown per segment finns i drawern och matris-fliken.",
  },
  {
    id: "planering",
    label: CATEGORY_LABELS.Communication,
    numeric: true,
    tooltip:
      "Planeringsaktiviteter — intents och reminders. Räkneraden visar totalt antal. Slutförd betyder att planeringen är kvitterad, inte att mötet ägt rum.",
  },
  {
    id: "motesbokning",
    label: CATEGORY_LABELS.Followup,
    numeric: true,
    tooltip:
      "Mötesbokning — konkret bokade aktiviteter. Detta representerar faktiskt utfört/bokat arbete.",
  },
  {
    id: "contactAttempts",
    label: "Försök",
    numeric: true,
    tooltip:
      "Kontaktförsök — antal gånger säljaren försökt nå kunder utan att lyckas. Ren ansträngningsmetrik, ingår inte i någon total.",
  },
  { id: "overdue", label: "Overdue", numeric: true },
  { id: "backlog", label: "Backlog", numeric: true },
];

const SalespersonTable: React.FC<SalespersonTableProps> = ({
  data,
  onRowClick,
}) => {
  const theme = useTheme();
  // Default-sort är nu på Mötesbokning eftersom det representerar faktiskt
  // utfört arbete — Planering är bara intents och hade skiftat default-ordningen
  // till att favorisera personer som bara skriver ner intentioner.
  const [sortKey, setSortKey] = useState<SortKey>("motesbokning");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const rows: Row[] = useMemo(() => {
    return data.map((stats) => {
      const totals = getGrandTotal(stats.Breakdown);
      const planering = getPlaneringTotal(stats.Breakdown);
      const motesbokning = getMotesbokningTotal(stats.Breakdown);
      const prospectsCounts = sumActivityCounts(
        getSegmentTotal(stats.Breakdown, "SuspectProspect"),
        getSegmentTotal(stats.Breakdown, "Pipeline")
      );
      return {
        stats,
        name: stats.SalespersonName,
        existing: getSegmentTotal(stats.Breakdown, "ExistingCustomer").Total,
        prospects: prospectsCounts.Total,
        planering: planering.Total,
        planeringCompleted: planering.Completed,
        planeringRate: planering.CompletionRate,
        motesbokning: motesbokning.Total,
        motesbokningCompleted: motesbokning.Completed,
        motesbokningRate: motesbokning.CompletionRate,
        contactAttempts: getContactAttemptCount(stats.Breakdown.Total.Total),
        total: totals.Total,
        overdue: totals.Overdue,
        backlog: stats.Breakdown.CarriedOverBacklog,
      };
    });
  }, [data]);

  const sortedRows = useMemo(() => {
    const sorted = [...rows].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === "string" && typeof bv === "string") {
        return av.localeCompare(bv, "sv");
      }
      return (av as number) - (bv as number);
    });
    return sortDir === "desc" ? sorted.reverse() : sorted;
  }, [rows, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "name" ? "asc" : "desc");
    }
  };

  if (data.length === 0) {
    return (
      <Paper sx={{ p: 6, borderRadius: 3, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          Inga säljare hittades
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Justera filtren för att se data
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: 3,
        boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.06)}`,
        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
      }}
    >
      <Table sx={{ minWidth: 760 }} size="small">
        <TableHead>
          <TableRow
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              "& th": { fontWeight: 700, whiteSpace: "nowrap", py: 1.25 },
            }}
          >
            {headCells.map((cell) => {
              const sortLabel = (
                <TableSortLabel
                  active={sortKey === cell.id}
                  direction={sortKey === cell.id ? sortDir : "asc"}
                  onClick={() => handleSort(cell.id)}
                  IconComponent={
                    sortDir === "desc" ? ArrowDownIcon : ArrowUpIcon
                  }
                >
                  {cell.label}
                </TableSortLabel>
              );
              const headerTooltip =
                cell.id === "backlog"
                  ? BACKLOG_TOOLTIP
                  : cell.id === "overdue"
                  ? OVERDUE_TOOLTIP
                  : cell.tooltip ?? null;
              return (
                <TableCell
                  key={cell.id}
                  align={cell.numeric ? "right" : "left"}
                  sortDirection={sortKey === cell.id ? sortDir : false}
                  sx={
                    cell.id === "contactAttempts"
                      ? {
                          borderLeft: `1px dashed ${alpha(
                            CONTACT_ATTEMPT_COLOR,
                            0.4
                          )}`,
                        }
                      : cell.id === "name"
                      ? { width: 180, maxWidth: 180 }
                      : undefined
                  }
                >
                  {headerTooltip ? (
                    <Tooltip title={headerTooltip} arrow placement="top">
                      <span>{sortLabel}</span>
                    </Tooltip>
                  ) : (
                    sortLabel
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.map((row) => {
            const planeringColor = getCompletionRateColor(
              row.planeringRate,
              theme
            );
            const motesbokningColor = getCompletionRateColor(
              row.motesbokningRate,
              theme
            );
            return (
              <TableRow
                key={row.stats.SalespersonId}
                hover
                onClick={() => onRowClick(row.stats)}
                sx={{
                  cursor: "pointer",
                  transition: "background-color 0.15s",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                  },
                }}
              >
                <TableCell sx={{ maxWidth: 180, width: 180 }}>
                  <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Avatar
                      src={row.stats.SalespersonImagePath}
                      sx={{
                        width: 30,
                        height: 30,
                        fontSize: "0.85rem",
                        flexShrink: 0,
                      }}
                    >
                      {row.name.charAt(0)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          lineHeight: 1.25,
                        }}
                      >
                        {row.name}
                      </Typography>
                      {row.stats.TeamName && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            lineHeight: 1.25,
                          }}
                        >
                          {row.stats.TeamName}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{row.existing}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.total > 0
                      ? `${Math.round((row.existing / row.total) * 100)}%`
                      : "—"}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{row.prospects}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.total > 0
                      ? `${Math.round((row.prospects / row.total) * 100)}%`
                      : "—"}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 104 }}>
                  <CategoryCell
                    total={row.planering}
                    completed={row.planeringCompleted}
                    rate={row.planeringRate}
                    color={planeringColor}
                    theme={theme}
                  />
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 104 }}>
                  <CategoryCell
                    total={row.motesbokning}
                    completed={row.motesbokningCompleted}
                    rate={row.motesbokningRate}
                    color={motesbokningColor}
                    theme={theme}
                  />
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ borderLeft: `1px dashed ${alpha(CONTACT_ATTEMPT_COLOR, 0.4)}` }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color:
                        row.contactAttempts === 0
                          ? "text.disabled"
                          : CONTACT_ATTEMPT_COLOR,
                      fontWeight: row.contactAttempts > 0 ? 600 : 400,
                    }}
                  >
                    {row.contactAttempts}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {row.overdue > 0 ? (
                    <Tooltip title={OVERDUE_TOOLTIP} arrow placement="top">
                      <Chip
                        label={row.overdue}
                        size="small"
                        color="error"
                        variant="outlined"
                      />
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      0
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  {row.backlog > 0 ? (
                    <Tooltip title={BACKLOG_TOOLTIP} arrow placement="top">
                      <Chip
                        label={row.backlog}
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      0
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Cell för en kategori (Planering eller Mötesbokning) — visar
// completed/total som huvudtal + CR%-progressbar som visuell indikator.
const CategoryCell: React.FC<{
  total: number;
  completed: number;
  rate: number;
  color: string;
  theme: Theme;
}> = ({ total, completed, rate, color, theme }) => {
  if (total === 0) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  return (
    <Stack spacing={0.5} alignItems="flex-end">
      <Typography variant="body2" fontWeight="bold">
        {completed} / {total}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{ width: "100%", justifyContent: "flex-end" }}
      >
        <LinearProgress
          variant="determinate"
          value={rate}
          sx={{
            flex: 1,
            maxWidth: 52,
            height: 5,
            borderRadius: 3,
            bgcolor: alpha(theme.palette.grey[300], 0.3),
            "& .MuiLinearProgress-bar": {
              borderRadius: 3,
              bgcolor: color,
            },
          }}
        />
        <Typography
          variant="caption"
          sx={{ color, fontWeight: 600, minWidth: 36, textAlign: "right" }}
        >
          {formatPercent(rate, 0)}
        </Typography>
      </Stack>
    </Stack>
  );
};

export default SalespersonTable;
